import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../helpers/api_helper';
import { EarningHistory, EarningInfoType, EarningsState } from './types';
import { toast } from 'react-toastify';
import { handleValidDate, handleValidTime } from '../../../helpers/helpers';
import { statusForTable } from '../../Bonuses/MatchingBonus/slice';

export const getEarningsInfo = createAsyncThunk(
  'earnings/info/fetch',
  async (userId: string) => {
    const response = await axiosInstance.get('/mlm/user/earnings/info', {
      params: { userId }
    });

    return response.data.data as EarningInfoType;
  }
);

export const getEarningsHistory = createAsyncThunk(
  'earnings/history/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get('/mlm/user/earnings/history', {
      params
    });
    return response.data.data;
  }
);

export const exportEarnings = createAsyncThunk(
  'earnings/exportEarnings',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/mlm/user/earnings/history/export',
      {
        params
      }
    );

    try {
      if (response?.data?.data?.earnings) {
        const dataForExport = response?.data?.data?.earnings.map((u: any) => {
          return {
            id: u?.id,
            userId: u?.bonusUserId,
            type: u?.name,
            username: u?.username,
            amount: u?.amount,
            dateCreated: u?.dateCreated
              ? `${handleValidDate(u.dateCreated)}, ${handleValidTime(
                  u.dateCreated
                )}`
              : '',
            status: statusForTable(u?.status)
          };
        });

        return dataForExport;
      }

      if (response.data.message) {
        toast.success(response.data.message);
      }
    } catch (error: any) {
      toast.error(error.response.data.error);
    }
  }
);

export const exportHeaders = [
  { label: 'Bonus ID', key: 'id' },
  { label: 'User', key: 'userId' },
  { label: 'Type', key: 'type' },
  { label: 'Username', key: 'username' },
  { label: 'Amount', key: 'amount' },
  { label: 'Date Created', key: 'dateCreated' },
  { label: 'Status', key: 'status' }
];

export const paramsForExport = (searchParams: URLSearchParams) => {
  const params = searchParams;
  params.delete('countPerPage');
  params.delete('numberOfPage');
  params.delete('sortBy');
  params.delete('sortOrder');
  return params;
};

const initialState = {
  info: null,
  history: [],
  loading: false,
  dataForExport: [],
  loadingExport: false,
  totalCount: 0
} as EarningsState;

const earningsSlice = createSlice({
  name: 'earnings',
  initialState,
  reducers: {
    removeEarningsForExport(state) {
      state.dataForExport = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getEarningsInfo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getEarningsInfo.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.info = payload;
      // state.totalCount = payload.count;
    });
    builder.addCase(getEarningsInfo.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getEarningsHistory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getEarningsHistory.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.history = payload.earnings as EarningHistory[];
      state.totalCount = payload.count as number;
    });
    builder.addCase(getEarningsHistory.rejected, (state) => {
      state.loading = false;
    });

    //loading export
    builder.addCase(exportEarnings.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportEarnings.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportEarnings.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { removeEarningsForExport } = earningsSlice.actions;

export default earningsSlice.reducer;
